'use client';

import type { SortingState } from '@tanstack/react-table';
import { useTranslations } from 'next-intl';
import { ArrowUpDown, Check, ArrowUp, ArrowDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface SortOption {
  label: string;
  value: string;
}

interface SortControlProps {
  options: SortOption[];
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
}

export function SortControl({
  options,
  sorting,
  onSortingChange
}: SortControlProps) {
  const tc = useTranslations('common');

  const currentSort = sorting[0];
  const currentField = currentSort?.id ?? '';
  const currentDesc = currentSort?.desc ?? false;

  const currentLabel =
    options.find((o) => o.value === currentField)?.label ?? tc('sortBy');

  function handleFieldChange(value: string) {
    onSortingChange([{ id: value, desc: currentDesc }]);
  }

  function handleDirectionToggle() {
    if (!currentField) return;
    onSortingChange([{ id: currentField, desc: !currentDesc }]);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='outline' size='sm' className='h-9 gap-1.5'>
          <ArrowUpDown className='h-4 w-4' />
          <span className='hidden sm:inline'>{currentLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-48'>
        <DropdownMenuLabel>{tc('sortBy')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {options.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleFieldChange(option.value)}
            >
              <Check
                className={cn(
                  'me-2 h-4 w-4',
                  currentField === option.value ? 'opacity-100' : 'opacity-0'
                )}
              />
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
        {currentField && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleDirectionToggle}>
              {currentDesc ? (
                <ArrowDown className='me-2 h-4 w-4' />
              ) : (
                <ArrowUp className='me-2 h-4 w-4' />
              )}
              {currentDesc ? tc('descending') : tc('ascending')}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
